/**
 * Linking a game account, and deciding what it shows.
 *
 * Riot is the only one wired to anything real. The rest of the providers are
 * still nothing but a name, so they say so rather than offering a button that
 * opens a page that does not exist.
 *
 * Linked by Riot ID, and shown as a claim rather than a proof.
 *
 * This used to hand out a code to paste into the League client's Verification
 * tab, which the server then read back through Riot to prove the account was
 * really yours. Riot removed both that tab and the endpoint behind it in March
 * 2022, so the step could never complete - it only sent people hunting through
 * a settings screen for something that is not there.
 *
 * Riot Sign On is the only sanctioned proof now, and it needs an application
 * Riot has approved. Until that exists a link says "this person says this is
 * their account", and the interface does not pretend otherwise.
 *
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { openExternal } from '@/lib/bridge';
import type { Connection } from '@/lib/types';
import { Icon } from '@/icons/Icon';
import { Button, Card, CardHead, Field, Input, Tag, Toggle, useConfirm, useToast } from './ui';
import { Select } from './Select';

type FieldDef = { id: string; label: string; note?: string; on: boolean };
type GameDef = { id: string; name: string; available: boolean };

type Info = {
  providers: {
    riot: {
      available: boolean;
      sso: boolean;
      regions: { id: string; name: string }[];
      fields: { account: FieldDef[]; perGame: FieldDef[] };
      games: GameDef[];
    };
  };
  connections: Record<string, Connection>;
};

/** What each game is called, and the colour it wears. */
const GAME_LOOK: Record<string, { name: string; short: string; colour: string }> = {
  lol: { name: 'League of Legends', short: 'LoL', colour: '#c8963e' },
  tft: { name: 'Teamfight Tactics', short: 'TFT', colour: '#7b68d4' },
  val: { name: 'VALORANT', short: 'VAL', colour: '#ff4655' }
};

/** The ones that exist as an idea and nothing more. */
const SOON = [
  { id: 'steam', name: 'Steam', note: 'Library and playtime', colour: '#66c0f4' },
  { id: 'twitch', name: 'Twitch', note: 'Show when you are live', colour: '#9146ff' },
  { id: 'xbox', name: 'Xbox', note: 'Gamertag and achievements', colour: '#107c10' },
  { id: 'psn', name: 'PlayStation', note: 'Online ID and trophies', colour: '#0070d1' }
];

export function ConnectionsTab() {
  const { data, isPending } = useQuery({
    queryKey: ['connections'],
    queryFn: () => api.get<Info>('/api/connections')
  });

  if (isPending || !data) {
    return <Card><div className="h-[220px] animate-pulse rounded-[var(--radius)] bg-surface-1" /></Card>;
  }

  return (
    <div className="flex flex-col gap-[var(--gap)]">
      <Riot info={data} />

      <DiscordCard info={data} />

      <Card>
        <CardHead title="Coming later" note="Nothing behind these yet — they are listed so you know what is planned." />
        <div className="flex flex-col gap-1.5">
          {SOON.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3 opacity-60">
              <span
                className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-[14px] font-extrabold text-white"
                style={{ background: p.colour }}
              >
                {p.name[0]}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-semibold">{p.name}</span>
                <span className="block text-[12px] text-mute">{p.note}</span>
              </span>
              <Tag>SOON</Tag>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------- riot */

/**
 * Discord, in Connected Accounts.
 *
 * Worth reading beside the Riot card above it, because the two are not the
 * same kind of link and the interface should not pretend they are. Riot took
 * away the only way an app could prove an account was yours, so that one is a
 * claim. Discord has real OAuth, so this one is proof - and it says so.
 *
 * The whole flow happens in a browser: we ask the server for an authorize
 * URL, open it, and the server handles the callback. Nothing sensitive passes
 * through here, and no Discord token is ever kept.
 */
function DiscordCard({ info }: { info: Info }) {
  const link = info.connections.discord;
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);

  /* The callback lands in a browser tab, not in here, so the app has to be
     told to look again. Checking on focus is the least surprising way: you
     come back to Pugo and it is linked. */
  useEffect(() => {
    const onFocus = () => void queryClient.invalidateQueries({ queryKey: ['connections'] });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [queryClient]);

  async function connect() {
    setBusy(true);
    try {
      const res = await api.get<{ url: string }>('/api/connections/discord/authorize');
      openExternal(res.url);
      toast('Approve it in your browser, then come back', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Ask Discord what the name and picture are now.
   *
   * Needed because nothing about a link updates on its own: Pugo keeps no
   * Discord token, so it cannot notice a change and has to be told to look.
   * The server answers plainly when it has no way to look, which is better
   * than a button that appears to work and changes nothing.
   */
  async function refreshLink() {
    setBusy(true);
    try {
      await api.post('/api/connections/discord/refresh', {});
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      toast('Discord profile updated', 'good');
    } catch (err) {
      /* No bot on this server to look it up: ask Discord directly instead.
         An account that already approved Pugo goes straight through, and
         the new name and picture are here when you come back. */
      if (err instanceof ApiError && err.code === 'no_bot') {
        try {
          const res = await api.get<{ url: string }>('/api/connections/discord/authorize?refresh=1');
          openExternal(res.url);
          toast('Refreshing through Discord - come back to Pugo when the tab says done', 'good');
        } catch (inner) {
          toast((inner as Error).message, 'bad');
        }
      } else {
        toast((err as Error).message, 'bad');
      }
    } finally {
      setBusy(false);
    }
  }

  async function unlink() {
    if (!await confirm('Unlink Discord?', 'Anything using the link, like the Discord bot, stops working for you.',
      { confirmLabel: 'Unlink', danger: true })) return;
    try {
      await api.del('/api/connections/discord');
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      toast('Discord unlinked', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  if (link) {
    return (
      <Card>
        <CardHead
          title="Discord"
          note="Linked, and proved through Discord's own sign-in"
          actions={
            <span className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => void refreshLink()}
                disabled={busy}
                className="text-[12.5px] font-semibold text-mute transition-colors hover:text-accent-hi disabled:opacity-50"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={() => void unlink()}
                className="text-[12.5px] font-semibold text-mute transition-colors hover:text-bad"
              >
                Unlink
              </button>
            </span>
          }
        />
        <div className="flex items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-2 p-3">
          {link.avatar ? (
            /* Their real Discord picture. Rounded like every other tile in the
               app rather than a circle, which is reserved for faces on Pugo. */
            <img src={link.avatar} alt="" className="h-11 w-11 flex-none rounded-[var(--radius)] object-cover" />
          ) : (
            <span
              className="grid h-11 w-11 flex-none place-items-center rounded-[var(--radius)] text-[15px] font-extrabold text-white"
              style={{ background: '#5865f2' }}
            >
              D
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-bold">{link.username}</span>
            <span className="block text-[12px] text-mute">
              Linked {new Date(link.linkedAt).toLocaleDateString()}
            </span>
          </span>
          <Tag kind="good">VERIFIED</Tag>
        </div>
        <p className="mt-2 text-[12px] text-mute">
          The Discord bot can open parties for you with <code className="font-mono">/lfg</code>.
        </p>
        {dialog}
      </Card>
    );
  }

  return (
    <Card>
      <CardHead title="Discord" note="Use the Pugo bot from your server, and show your Discord name on your profile" />
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] text-[17px] font-extrabold text-white"
          style={{ background: '#5865f2' }}
        >
          D
        </span>
        <p className="min-w-[200px] flex-1 text-[12.5px] text-mute">
          You approve this in Discord, so unlike the Riot link above it really does prove the
          account is yours. Pugo reads your name and picture once and keeps no Discord token.
        </p>
        <Button kind="primary" disabled={busy} onClick={() => void connect()}>
          {busy ? 'Opening…' : 'Connect Discord'}
        </Button>
      </div>
      {dialog}
    </Card>
  );
}

function Riot({ info }: { info: Info }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const riot = info.providers.riot;
  const link = info.connections.riot;

  const [riotId, setRiotId] = useState('');
  const [region, setRegion] = useState('euw1');
  
  const [busy, setBusy] = useState(false);

  /** Replaces the cached answer, so a change is on screen at once. */
  const settle = (connection: Connection) => {
    queryClient.setQueryData<Info>(['connections'], (old) =>
      old ? { ...old, connections: { ...old.connections, riot: connection } } : old);
    // The profile shows the same data, so it has to be told as well.
    void queryClient.invalidateQueries({ queryKey: ['user'] });
  };

  async function start() {
    setBusy(true);
    try {
      const res = await api.post<{ connection: Connection }>(
        '/api/connections/riot/start', { riotId: riotId.trim(), region });
      settle(res.connection);
      setRiotId('');
      toast('Riot account linked', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function unlink() {
    const ok = await confirm(
      'Unlink your Riot account?',
      'Your rank and level come off your profile. You can link it again whenever you like.',
      { confirmLabel: 'Unlink', danger: true }
    );
    if (!ok) return;
    await api.del('/api/connections/riot');
    await queryClient.invalidateQueries({ queryKey: ['connections'] });
    void queryClient.invalidateQueries({ queryKey: ['user'] });
    toast('Unlinked', 'good');
  }

  const refresh = useMutation({
    mutationFn: () => api.post<{ connection: Connection }>('/api/connections/riot/refresh', {}),
    onSuccess: (res) => { settle(res.connection); toast('Up to date', 'good'); },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  /* ---- already linked ---- */
  if (link?.verified) {
    return (
      <>
        <Card>
          <CardHead
            title="Riot Games"
            note="League of Legends and Teamfight Tactics"
            actions={<Tag kind="good">LINKED</Tag>}
          />

          <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3.5">
            <span
              className="grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] text-[16px] font-extrabold text-white"
              style={{ background: '#d13639' }}
            >
              R
            </span>
            <span className="min-w-[160px] flex-1">
              <span className="block text-[14px] font-bold">{link.riotId ?? 'Riot account'}</span>
              <span className="block text-[12px] text-mute">
                {[link.region, gameList(link)].filter(Boolean).join(' · ')}
              </span>
            </span>
            <Button kind="outline" size="sm" disabled={refresh.isPending || !riot.available} onClick={() => refresh.mutate()}>
              {refresh.isPending ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button kind="danger" size="sm" onClick={() => void unlink()}>Unlink</Button>
          </div>

          {Object.entries(link.games ?? {}).map(([game, stats]) => (
            <GameRow key={game} game={game} stats={stats} />
          ))}

          {!Object.keys(link.games ?? {}).length ? (
            <p className="mt-2.5 text-[12.5px] text-mute">
              Nothing to show yet — press Refresh once you have played a ranked game.
            </p>
          ) : null}

          {link.fetchedAt ? (
            <p className="mt-2 text-[11.5px] text-ghost">Last checked {ago(link.fetchedAt)}.</p>
          ) : null}
        </Card>

        <Visibility fields={riot.fields} games={riot.games} link={link} onSettled={settle} />
        {dialog}
      </>
    );
  }

  /* ---- nothing linked, and the server cannot look anything up ---- */
  if (!riot.available) {
    return (
      <Card>
        <CardHead title="Riot Games" note="League of Legends and Teamfight Tactics" />
        <div className="flex items-start gap-3 rounded-[var(--radius)] border border-warn bg-[color-mix(in_srgb,var(--warn)_9%,transparent)] p-3.5">
          <Icon name="alert" size={16} className="mt-0.5 flex-none text-warn" />
          <p className="text-[12.5px] leading-relaxed text-dim">
            This server has no Riot API key, so it cannot look anything up yet. Everything else
            here is ready — add <code className="font-mono text-[12px]">RIOT_API_KEY</code> to the
            server and this turns on.
          </p>
        </div>
        {dialog}
      </Card>
    );
  }

  /* ---- not linked ---- */
  return (
    <Card>
      <CardHead title="Riot Games" note="Show your rank, level and most-played champions on your profile" />

      <div className="grid gap-[var(--gap)] sm:grid-cols-[1fr_200px]">
        <Field label="Riot ID" hint="Exactly as it appears in the client, tag included.">
          <Input
            value={riotId}
            onChange={(e) => setRiotId(e.target.value)}
            placeholder="Name#TAG"
            spellCheck={false}
            onKeyDown={(e) => { if (e.key === 'Enter' && riotId.includes('#')) void start(); }}
          />
        </Field>
        <Field label="Region">
          <Select
            value={region}
            onChange={setRegion}
            options={riot.regions.map((r) => ({ value: r.id, label: r.name }))}
          />
        </Field>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="min-w-[200px] flex-1 text-[12px] text-mute">
          Riot removed the way apps used to prove an account is yours, so this is shown as a
          claim rather than a verified link until Riot approves sign-in for Pugo.
        </span>
        <Button kind="primary" disabled={busy || !riotId.includes('#')} onClick={() => void start()}>
          {busy ? 'Looking…' : 'Link account'}
        </Button>
      </div>

      {dialog}
    </Card>
  );
}

/**
 * What other people see.
 *
 * Saved on the switch rather than behind a Save button, and the row is held
 * disabled until the server answers - so a failed save cannot leave the
 * screen claiming something is hidden when it is not.
 */
function Visibility({ fields, games, link, onSettled }: {
  fields: { account: FieldDef[]; perGame: FieldDef[] };
  games: GameDef[];
  link: Connection;
  onSettled: (next: Connection) => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState<string | null>(null);
  const show = link.show ?? {};

  async function set(key: string, on: boolean) {
    setSaving(key);
    try {
      const res = await api.patch<{ connection: Connection }>(
        '/api/connections/riot/visibility', { [key]: on }
      );
      onSettled(res.connection);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setSaving(null);
    }
  }

  /* Only games this account actually plays are worth switches. */
  const played = games.filter((g) => link.games && g.id in link.games);

  return (
    <Card>
      <CardHead
        title="What people see"
        note="Every switch saves the moment you flip it — there is nothing to save."
      />

      <span className="mb-1 block text-[10.5px] font-bold tracking-[.07em] text-mute uppercase">
        Your account
      </span>
      <div className="flex flex-col divide-y divide-line-soft">
        {fields.account.map((f) => (
          <Toggle
            key={f.id}
            checked={!!show[f.id]}
            disabled={saving === f.id}
            onChange={(on) => void set(f.id, on)}
            label={f.label}
            note={f.note}
          />
        ))}
      </div>

      {played.map((g) => {
        const look = GAME_LOOK[g.id];
        const on = !!show[g.id];

        return (
          <div key={g.id} className="mt-[var(--gap)] border-t border-line-soft pt-[var(--gap)]">
            {/* The game's own switch. Off, and none of its detail is sent at
                all - so the rows underneath are hidden rather than merely
                disabled, because there is nothing left to decide. */}
            <Toggle
              checked={on}
              disabled={saving === g.id}
              onChange={(next) => void set(g.id, next)}
              label={look?.name ?? g.name}
              note={on ? undefined : 'Hidden from your profile entirely'}
            />

            {on ? (
              <div className="mt-1 flex flex-col divide-y divide-line-soft border-l-2 pl-3.5"
                style={{ borderColor: look?.colour ?? 'var(--line)' }}>
                {fields.perGame
                  // Champion mastery is a League idea; the others have no
                  // equivalent, so the switch would control nothing.
                  .filter((f) => f.id !== 'champions' || g.id === 'lol')
                  .map((f) => {
                    const key = g.id + '.' + f.id;
                    return (
                      <Toggle
                        key={key}
                        checked={!!show[key]}
                        disabled={saving === key}
                        onChange={(next) => void set(key, next)}
                        label={f.label}
                        note={f.note}
                      />
                    );
                  })}
              </div>
            ) : null}
          </div>
        );
      })}

      <p className="mt-[var(--gap)] flex items-start gap-2 border-t border-line-soft pt-[var(--gap)] text-[12px] text-mute">
        <Icon name="lock" size={13} className="mt-0.5 flex-none opacity-70" />
        Anything switched off is never sent to anybody else — it is filtered on the server, not
        hidden in the page.
      </p>
    </Card>
  );
}

/** One game's ranks, inside the linked card. */
function GameRow({ game, stats }: { game: string; stats: NonNullable<Connection['games']>[string] }) {
  const look = GAME_LOOK[game];
  const ranks = (stats.ranks ?? []).filter((r) => r.tier);

  return (
    <div className="mt-2.5">
      <span className="mb-1.5 flex items-center gap-2">
        <span
          className="rounded-[calc(6px*var(--radius-mul,1))] px-1.5 py-0.5 text-[9.5px] font-extrabold tracking-[.06em] text-white"
          style={{ background: look?.colour ?? 'var(--surface-4)' }}
        >
          {look?.short ?? game.toUpperCase()}
        </span>
        <span className="text-[12.5px] font-semibold text-dim">{look?.name ?? game}</span>
        {stats.level ? <span className="text-[11.5px] text-ghost">Level {stats.level}</span> : null}
      </span>

      {ranks.length ? (
        <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
          {ranks.map((r) => (
            <div key={r.queue} className="rounded-[var(--radius-s)] border border-line-soft bg-surface-1 px-3 py-2.5">
              <div className="text-[10px] font-extrabold tracking-[.1em] text-ghost uppercase">{r.queue}</div>
              <div className="mt-0.5 text-[15px] font-bold" style={{ color: tierColour(r.tier) }}>
                {tidy(r.tier!)}{r.rank ? ' ' + r.rank : ''}
              </div>
              <div className="text-[11.5px] text-mute">
                {r.points ? r.points + ' LP' : ''}
                {r.wins !== undefined && r.losses !== undefined
                  ? (r.points ? ' · ' : '') + r.wins + 'W ' + r.losses + 'L' + winrate(r.wins, r.losses)
                  : ''}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-ghost">Unranked</p>
      )}
    </div>
  );
}


/** GOLD -> Gold. Riot shouts its tiers; the rest of the app does not. */
function tidy(tier: string): string {
  return tier.charAt(0) + tier.slice(1).toLowerCase();
}

function ago(at: number): string {
  const mins = (Date.now() - at) / 60_000;
  if (mins < 1) return 'just now';
  if (mins < 60) return Math.floor(mins) + ' minutes ago';
  if (mins < 1440) return Math.floor(mins / 60) + ' hours ago';
  return Math.floor(mins / 1440) + ' days ago';
}


/**
 * A linked Riot account, on somebody's profile.
 *
 * Renders whatever the server chose to send and nothing more - every field is
 * optional here precisely because the filtering already happened, so a hidden
 * rank is simply not in the object and there is no decision to make. A game
 * the owner has switched off never arrives at all.
 */
export function RiotCard({ link }: { link: Connection }) {
  const games = Object.entries(link.games ?? {});
  if (!link.riotId && !games.length) return null;

  return (
    <Card>
      <CardHead
        title="Riot Games"
        note={link.region ?? undefined}
        actions={<Tag kind="good">VERIFIED</Tag>}
      />

      <div className="flex flex-wrap items-center gap-3">
        <span
          className="grid h-12 w-12 flex-none place-items-center rounded-[calc(14px*var(--radius-mul,1))] text-[17px] font-extrabold text-white"
          style={{ background: '#d13639' }}
        >
          R
        </span>
        <span className="min-w-0 flex-1">
          {link.riotId ? (
            <span className="selectable block text-[15px] font-bold">{link.riotId}</span>
          ) : (
            <span className="block text-[15px] font-bold text-mute">Riot account</span>
          )}
          <span className="block text-[12.5px] text-mute">
            {games.length ? gameList(link) : 'No games shown'}
          </span>
        </span>
      </div>

      {games.map(([game, stats]) => (
        <GameRow key={game} game={game} stats={stats} />
      ))}
    </Card>
  );
}

/** "LoL - TFT - VAL", for the line under the name. */
function gameList(link: Connection): string {
  return Object.keys(link.games ?? {})
    .map((g) => GAME_LOOK[g]?.short ?? g.toUpperCase())
    .join(' \u00b7 ');
}

/** The colours Riot uses for its tiers, near enough to be recognised. */
function tierColour(tier: string | null | undefined): string {
  const map: Record<string, string> = {
    IRON: '#7c7473', BRONZE: '#a4633f', SILVER: '#9fadbb', GOLD: '#e0a92b',
    PLATINUM: '#4cb5a3', EMERALD: '#2fa76a', DIAMOND: '#7c9bff',
    MASTER: '#b26bff', GRANDMASTER: '#e05a5a', CHALLENGER: '#5ec7f0'
  };
  return map[String(tier ?? '').toUpperCase()] ?? 'var(--ink)';
}

function winrate(wins: number, losses: number): string {
  const total = wins + losses;
  if (total < 10) return '';
  return ' · ' + Math.round((wins / total) * 100) + '%';
}
